import HeaderProps from "./_header-props";
import HeaderBackground from "./_header-background";
import HeaderShared from "./_header-shared";
import HeaderButtonColors from "./_header-button-colors";
import HeaderLogo from "./_header-logo";
import HeaderFocusManager from "./_header-focus-manager";
import HeaderMobile from "./_header-mobile";

class _headerToggles {
  /**
   * Connects all the header buttons to the appropriate list transitions
   * @param {HTMLElement} $toggle
   */
  connectHeaderToggleToListTransitions($toggle) {
    // get the target id from the data-header-toggle attribute
    // because these toggles are not inside the actual transition list,
    // they should contain the id of the transition list
    const target = $toggle.dataset.headerToggle;

    // The target element has to be found through the id stored in the target submenu.
    // because the target of the header toggle could be a child of the transition list,
    // the closest transition list upstream is searched for
    const $target = HeaderProps.$header
      .querySelector(`#${target}`)
      ?.closest("[data-list-transition]");

    // set the custom value for the data-list-transition-item attribute on sub menu toggles
    this.setupCustomDelayValuesForSubMenuToggles($target);

    $toggle.addEventListener("click", (_) => {
      // if the toggle has no target, an error is thrown to the console
      console.assert(target, "data-header-toggle must have a target");

      // the root-nav is handled in another place
      if (target === "root-nav") {
        return;
      }

      const currentOpenSublist = this.getOpenSublist();
      const isClosingCurrentSubmenu = target === currentOpenSublist;

      if (isClosingCurrentSubmenu) {
        HeaderProps.$header.dataset.headerToggleState = '';
      } else {
        HeaderProps.$header.dataset.headerToggleState = target;
      }

      this.toggleNavAccessibility(target, currentOpenSublist, isClosingCurrentSubmenu, false);


      // if $target was not found, warn the user and stop the method from further execution
      if (!$target) {
        console.warn(`Could not find target for header toggle`);
        return;
      }

      const state = $target.triggerTransition();
      if (state === "open") {
        const $rootNav = HeaderProps.$header.querySelector("#root-nav");
        $rootNav.markListAsOpen();
        HeaderLogo.switchToDetailedLogo();
      }

      HeaderButtonColors.setVisualStateOfDesktopToggles(target, state);

      const mobileNavActive = HeaderMobile.isMobile();
      const sublistsHaveToggle = !!$target.querySelector(
        "[data-list-transition-toggle]"
      );
      if (mobileNavActive && sublistsHaveToggle) {
        $target.closeSublists();
      }

      // if the target is the element that is opening, then close other sections
      HeaderShared.closeSiblingLists($target);

      // handle the collapsing of the header
      HeaderBackground.handleHeaderCollapse();
    });
  }

  /**
   * @return {string}
   */
  getOpenSublist() {
    return HeaderProps.$header.dataset.headerToggleState;
  }

  /**
   * toggles accessibility related functions
   * @param {string} target
   * @param {string|undefined} currentOpenSublist
   * @param {boolean} isClosingCurrentSubmenu
   * @param {boolean} isManualClose
   */
  toggleNavAccessibility(target, currentOpenSublist, isClosingCurrentSubmenu, isManualClose = false) {
    if (HeaderMobile.isMobile()) {
      return;
    }

    const $navContentBox = HeaderProps.$navContextBox;

    if (isManualClose) {
      const activeTarget = target || this.getOpenSublist();
      console.log('Manual close for:', activeTarget);

      if (activeTarget) {
        HeaderFocusManager.deactivateTrap(activeTarget);
      }
      $navContentBox.setAttribute("inert", "");

      HeaderProps.$header.dataset.headerToggleState = '';
      return;
    }

    if (isClosingCurrentSubmenu) {
      this._deactivateNavigation(target, $navContentBox);
    } else {
      if (currentOpenSublist) {
        HeaderFocusManager.deactivateTrap(currentOpenSublist);
      }

      if (target && HeaderFocusManager.hasTrap(target)) {
        this._activateNavigation(target, $navContentBox);
      } else {
        this._deactivateNavigation(target, $navContentBox);
      }
    }
  }

  /**
   * Activates navigation accessibility (removes inert, activates focus trap)
   * @private
   */
  _activateNavigation(target, $navContentBox) {
    $navContentBox.removeAttribute("inert");
    HeaderFocusManager.activateTrap(target);
  }

  /**
   * Deactivates navigation accessibility (adds inert, deactivates focus trap)
   * @private
   */
  _deactivateNavigation(target, $navContentBox) {
    HeaderFocusManager.deactivateTrap(target);
    $navContentBox.setAttribute("inert", "");
    HeaderProps.$header.dataset.headerToggleState = '';
  }

  /**
   * sets the custom value for the data-list-transition-item attribute on sub menu toggles
   * on desktop this will be: 'first'
   * on mobile this wil be the index of the toggle within the target (section menu)
   * @param {HTMLElement} $target
   */
  setupCustomDelayValuesForSubMenuToggles($target) {
    const $children = $target.querySelectorAll("[data-list-transition-item]");
    const $directChildren = [...$children].filter(
      ($child) => $child.closest("[data-list-transition]") === $target
    );
    const mobileNavActive = matchMedia(
      `(max-width: ${HeaderProps.breakPoint})`
    ).matches;
    $directChildren.forEach(($child, i) => {
      if (mobileNavActive) {
        $child.dataset.listTransitionItem = (i + 1).toString();
      } else {
        $child.dataset.listTransitionItem = "first";
      }
    });
  }

  /**
   * Makes context box inert
   */
  initializeContextBoxInert() {
    const $navContentBox = HeaderProps.$navContextBox;
    if ($navContentBox.hasAttribute("inert")) {
      return;
    }

    $navContentBox.setAttribute('inert', '')
  }

  /**
   * Sets up all the header toggles
   */
  setup() {
    const $headerToggles = HeaderProps.$header?.querySelectorAll(
      "[data-header-toggle]"
    );
    $headerToggles?.forEach(($toggle) => {
      if (
        $toggle.dataset.headerToggle === "root-nav" ||
        !$toggle.dataset.headerToggle
      ) {
        return;
      }
      if (!HeaderMobile.isMobile()) {
        HeaderFocusManager.registerTrap($toggle);
      }
      this.connectHeaderToggleToListTransitions($toggle);
    });

    if (!HeaderMobile.isMobile()) {
      this.initializeContextBoxInert();
    }
  }
}

export default new _headerToggles();
